import { useEffect } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';
import { useWorldCupTeamFlagStore } from '@/hooks/store/useWorldCupTeamFlagStore';

export interface WorldCupGroupTeamStats {
  played: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  goalsFor?: number | null;
  goalsAgainst?: number | null;
  advanceProbability: number | null;
  advanceStatus: string | null;
  advanceMarketSlug: string | null;
}

export interface WorldCupGroupTeam {
  name: string;
  countryKey: string;
  flagImage: string | null;
  teamColor: string | null;
  standingOrder: number;
  stats: WorldCupGroupTeamStats;
}

export interface WorldCupGroup {
  group: string;
  teams: WorldCupGroupTeam[];
}

export interface WorldCupGroupsData {
  groups: WorldCupGroup[];
}

export interface WorldCupGroupsResponse {
  success: boolean;
  data: WorldCupGroupsData;
  message?: string;
}

export interface UseWorldCupGroupsOptions {
  enabled?: boolean;
  swr?: SWRConfiguration<WorldCupGroupsResponse, Error>;
}

export async function fetchWorldCupGroups() {
  const response = await customFetch('/api/world-cup/group');

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch World Cup groups');
  }

  const result = (await response.json()) as WorldCupGroupsResponse;

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch World Cup groups');
  }

  return result;
}

export function useWorldCupGroups(options: UseWorldCupGroupsOptions = {}) {
  const enabled = options.enabled ?? true;
  const swrKey = enabled ? ['world-cup-groups'] : null;
  const setTeamFlagImages = useWorldCupTeamFlagStore((state) => state.setTeamFlagImages);

  const { data, error, isLoading, isValidating, mutate } = useSWR<WorldCupGroupsResponse, Error>(
    swrKey,
    fetchWorldCupGroups,
    {
      revalidateOnFocus: false,
      ...options.swr,
    },
  );

  const groups = data?.data?.groups ?? [];

  useEffect(() => {
    if (!groups.length) return;

    setTeamFlagImages(
      groups.flatMap((group) =>
        group.teams.map((team) => ({
          teamName: team.name,
          flagImage: team.flagImage,
        })),
      ),
    );
  }, [groups, setTeamFlagImages]);

  return {
    data: data ?? null,
    groups,
    list: groups,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useWorldCupGroups;
